import { canvasAabbOfRotatedPathBounds } from '@/lib/boundaries/bounding-box-boundary'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

const DISTRIBUTE_EPS = 1e-6

type Axis = 'x' | 'y'

type DistributeEntry = {
  shape: ShapeData
  start: number
  end: number
  size: number
  otherStart: number
  layerIndex: number
}

type DistributionOptions = {
  mode: 'auto' | 'manual'
  gap?: number
}

function canvasAabbForShape(
  shape: ShapeData,
  pathBounds: ShapeBounds | undefined
): { minX: number; minY: number; maxX: number; maxY: number } {
  // Pen paths (type === 'path') may not fill their placement rect — the SVG path
  // occupies only the region reported by getBBox(), which can have a non-zero x/y
  // offset. canvasAabbOfRotatedPathBounds ignores pathBounds for axis-aligned shapes
  // (to prevent stroke inflation for standard shapes), but that optimisation is wrong
  // for pen paths where the path genuinely does not fill the placement rect.
  if (shape.type === 'path' && pathBounds != null) {
    const rot = shape.rotation ?? 0
    const rNorm = ((rot % 360) + 360) % 360
    const isAxisAligned = rNorm <= 1e-4 || rNorm >= 360 - 1e-4

    if (isAxisAligned) {
      return {
        minX: shape.x + pathBounds.x,
        minY: shape.y + pathBounds.y,
        maxX: shape.x + pathBounds.x + pathBounds.width,
        maxY: shape.y + pathBounds.y + pathBounds.height,
      }
    }
  }

  return canvasAabbOfRotatedPathBounds({
    shapeX: shape.x,
    shapeY: shape.y,
    shapeWidth: shape.width,
    shapeHeight: shape.height,
    boundsX: pathBounds?.x ?? 0,
    boundsY: pathBounds?.y ?? 0,
    boundsWidth: pathBounds?.width ?? shape.width,
    boundsHeight: pathBounds?.height ?? shape.height,
    rotationDeg: shape.rotation ?? 0,
  })
}

function toEntry(
  shape: ShapeData,
  pathBounds: ShapeBounds | undefined,
  axis: Axis,
  layerIndex: number
): DistributeEntry {
  const aabb = canvasAabbForShape(shape, pathBounds)

  if (axis === 'x') {
    return {
      shape,
      start: aabb.minX,
      end: aabb.maxX,
      size: aabb.maxX - aabb.minX,
      otherStart: aabb.minY,
      layerIndex,
    }
  }

  return {
    shape,
    start: aabb.minY,
    end: aabb.maxY,
    size: aabb.maxY - aabb.minY,
    otherStart: aabb.minX,
    layerIndex,
  }
}

function compareEntries(a: DistributeEntry, b: DistributeEntry): number {
  return a.start - b.start || a.otherStart - b.otherStart || a.layerIndex - b.layerIndex
}

function roundPosition(value: number): number {
  return Math.round(value * 1000) / 1000
}

function buildDistributeItems(
  selectedShapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: Axis,
  layerOrder: string[],
  options: DistributionOptions
): BatchShapeTransformItem[] {
  const minCount = options.mode === 'auto' ? 3 : 2

  if (selectedShapes.length < minCount) {
    return []
  }

  const layerIndexById = new Map(layerOrder.map((id, index) => [id, index]))
  const entries = selectedShapes
    .filter((shape) => shape.hidden !== true && shape.locked !== true)
    .map((shape) =>
      toEntry(shape, getBounds(shape.id), axis, layerIndexById.get(shape.id) ?? layerOrder.length)
    )
    .sort(compareEntries)

  if (entries.length < minCount) {
    return []
  }

  const first = entries[0]
  const last = entries[entries.length - 1]
  const gap =
    options.mode === 'manual'
      ? (options.gap ?? 0)
      : (last.end - first.start - entries.reduce((sum, entry) => sum + entry.size, 0)) /
        (entries.length - 1)
  let cursor = first.start + first.size + gap
  const items: BatchShapeTransformItem[] = []
  const lastIndex = options.mode === 'auto' ? entries.length - 1 : entries.length

  for (let index = 1; index < lastIndex; index += 1) {
    const entry = entries[index]
    const delta = roundPosition(cursor - entry.start)

    if (Math.abs(delta) > DISTRIBUTE_EPS) {
      items.push({
        shapeId: entry.shape.id,
        before: { x: entry.shape.x, y: entry.shape.y },
        after: {
          x: axis === 'x' ? roundPosition(entry.shape.x + delta) : entry.shape.x,
          y: axis === 'y' ? roundPosition(entry.shape.y + delta) : entry.shape.y,
        },
      })
    }

    cursor += entry.size + gap
  }

  return items
}

export function buildAutoDistributeItems(
  selectedShapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: Axis,
  layerOrder: string[]
): BatchShapeTransformItem[] {
  return buildDistributeItems(selectedShapes, getBounds, axis, layerOrder, { mode: 'auto' })
}

export function buildManualDistributeItems(
  selectedShapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: Axis,
  layerOrder: string[],
  gap: number
): BatchShapeTransformItem[] {
  return buildDistributeItems(selectedShapes, getBounds, axis, layerOrder, { mode: 'manual', gap })
}
